/**
 * Issues API module
 *
 * Uses the same /issues endpoint as ticketsApi but with typed enum params
 * and a different response shape ({ items: Issue[] }).
 */
import type { Issue, CreateIssueRequest, UpdateIssueRequest, IssueSeverity, IssueStatus } from '@overlayqa/shared';
import { fetchApi, buildQuery } from './client';
import { log } from '@/lib/logger';
import { MUTATION_TIMEOUT_MS, SCREEN_READ_TIMEOUT_MS } from './requestTimeout';

log.info('[api/issues] Module loaded');

export const issuesApi = {
  list: (
    token: string | undefined,
    params?: { projectId?: string; status?: IssueStatus; severity?: IssueSeverity; type?: string }
  ) => {
    const query = buildQuery({
      projectId: params?.projectId,
      status: params?.status,
      severity: params?.severity,
      type: params?.type,
    });
    log.info('[api/issues] list', { query });
    return fetchApi<{ items: Issue[] }>(`/issues${query}`, { token, timeoutMs: SCREEN_READ_TIMEOUT_MS });
  },

  get: (token: string | undefined, id: string) =>
    fetchApi<Issue>(`/issues/${id}`, { token, timeoutMs: SCREEN_READ_TIMEOUT_MS }),

  // Saved-issue Copy text, rendered by the export formatter (2026-10-28). Ids in
  // table order; the server keeps it. Read-only, so viewers may use it too.
  // `html`: the clipboard's text/html part (2026-10-28); absent from a server
  // older than that change, which the copy treats as no HTML.
  copyText: (token: string | null | undefined, ids: string[], format: 'markdown' | 'plain-text') =>
    fetchApi<{ text: string; html?: string }>('/issues/copy', {
      method: 'POST',
      body: JSON.stringify({ ids, format }),
      token: token ?? undefined,
      timeoutMs: SCREEN_READ_TIMEOUT_MS,
    }),

  create: (token: string | undefined, userId: string, data: CreateIssueRequest) =>
    fetchApi<Issue>('/issues', {
      token,
      userId,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify(data),
    }),

  update: (token: string | undefined, id: string, data: UpdateIssueRequest) =>
    fetchApi<Issue>(`/issues/${id}`, {
      token,
      method: 'PATCH',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify(data),
      // 409: the server's compare-and-set gave up because a teammate kept
      // changing the row (routes/qaIssues.ts). The issue page toasts the
      // server's message; declaring it expected keeps the handled refusal
      // out of the error log (no fake $exception).
      expectedErrorStatuses: [409],
    }),

  delete: (token: string | undefined, id: string) =>
    fetchApi<{ deleted: boolean }>(`/issues/${id}`, {
      token,
      method: 'DELETE',
      timeoutMs: MUTATION_TIMEOUT_MS,
    }),
};
